import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase/config';
import { uploadLeadsCsv } from '../../api/adminService';
import './LeadSender.css';

const LeadSender = () => {
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const parseCSVPreview = (text) => {
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim());
    const nameIdx = headers.findIndex(h => h === '이름');
    const emailIdx = headers.findIndex(h => h === '이메일 주소');
    const phoneIdx = headers.findIndex(h => h === '전화번호');
    const formIdx = headers.findIndex(h => h === '양식');
    const dateIdx = headers.findIndex(h => h === '제출된 날짜');

    return lines.slice(1).map(line => {
      const cols = line.split(',').map(c => c.trim());
      return {
        name: nameIdx >= 0 ? cols[nameIdx] : '',
        email: emailIdx >= 0 ? cols[emailIdx] : '',
        phone: phoneIdx >= 0 ? cols[phoneIdx] : '',
        form: formIdx >= 0 ? cols[formIdx] : '',
        date: dateIdx >= 0 ? cols[dateIdx] : '',
      };
    });
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!selected.name.endsWith('.csv')) {
      setError('CSV 파일만 업로드 가능합니다');
      return;
    }

    setFile(selected);
    setError(null);
    setResult(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const rows = parseCSVPreview(text);
      setPreview(rows);
    };
    reader.readAsText(selected, 'utf-8');
  };

  const handleSend = async () => {
    if (!file) return;

    setSending(true);
    setError(null);
    setResult(null);

    try {
      const data = await uploadLeadsCsv(file);
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview([]);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (loading) {
    return (
      <div className="lead-sender">
        <div className="lead-sender__loading">로딩 중...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="lead-sender">
        <div className="lead-sender__error-box">관리자 로그인이 필요합니다.</div>
      </div>
    );
  }

  return (
    <div className="lead-sender">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>리드 CSV 발송 - Admin</title>
      </Helmet>

      <div className="lead-sender__header">
        <button className="lead-sender__back" onClick={() => navigate('/admin')}>
          관리자 홈
        </button>
        <div>
          <h1 className="lead-sender__title">리드 CSV 발송</h1>
          <p className="lead-sender__desc">Meta 광고 관리자에서 다운로드한 CSV를 업로드하면 이메일 + 알림톡이 자동 발송됩니다.</p>
        </div>
      </div>

      {/* 파일 업로드 영역 */}
      <div className="lead-sender__upload-area">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          className="lead-sender__file-input"
          id="csv-upload"
        />
        {!file ? (
          <label htmlFor="csv-upload" className="lead-sender__dropzone">
            <div className="lead-sender__dropzone-icon">CSV</div>
            <p className="lead-sender__dropzone-text">클릭하여 CSV 파일 선택</p>
            <p className="lead-sender__dropzone-hint">Meta 광고관리자 → 리드 → CSV 다운로드</p>
          </label>
        ) : (
          <div className="lead-sender__file-info">
            <span className="lead-sender__file-name">{file.name}</span>
            <span className="lead-sender__file-count">{preview.length}건</span>
            <button className="lead-sender__file-remove" onClick={handleReset}>변경</button>
          </div>
        )}
      </div>

      {/* 미리보기 */}
      {preview.length > 0 && !result && (
        <div className="lead-sender__preview">
          <h2 className="lead-sender__section-title">미리보기 ({preview.length}건)</h2>
          <div className="lead-sender__table-wrap">
            <table className="lead-sender__table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>이름</th>
                  <th>이메일</th>
                  <th>전화번호</th>
                  <th>양식</th>
                  <th>제출일</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{row.name}</td>
                    <td>{row.email}</td>
                    <td>{row.phone || '-'}</td>
                    <td className="lead-sender__cell-form">{row.form}</td>
                    <td>{row.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            className="lead-sender__send-btn"
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? '발송 중...' : `${preview.length}건 발송하기`}
          </button>
        </div>
      )}

      {/* 에러 */}
      {error && (
        <div className="lead-sender__error-box">{error}</div>
      )}

      {/* 결과 */}
      {result && (
        <div className="lead-sender__result">
          <h2 className="lead-sender__section-title">발송 결과</h2>

          <div className="lead-sender__summary">
            <div className="lead-sender__summary-item">
              <span className="lead-sender__summary-label">전체</span>
              <span className="lead-sender__summary-value">{result.summary.total}건</span>
            </div>
            <div className="lead-sender__summary-item lead-sender__summary-item--sent">
              <span className="lead-sender__summary-label">발송 성공</span>
              <span className="lead-sender__summary-value">{result.summary.sent}건</span>
            </div>
            <div className="lead-sender__summary-item lead-sender__summary-item--skipped">
              <span className="lead-sender__summary-label">스킵 (중복/테스트)</span>
              <span className="lead-sender__summary-value">{result.summary.skipped}건</span>
            </div>
            {result.summary.errors > 0 && (
              <div className="lead-sender__summary-item lead-sender__summary-item--error">
                <span className="lead-sender__summary-label">오류</span>
                <span className="lead-sender__summary-value">{result.summary.errors}건</span>
              </div>
            )}
          </div>

          <div className="lead-sender__table-wrap">
            <table className="lead-sender__table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>이름</th>
                  <th>이메일</th>
                  <th>상태</th>
                  <th>상세</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((r, i) => (
                  <tr key={i} className={`lead-sender__row--${r.status}`}>
                    <td>{i + 1}</td>
                    <td>{r.name}</td>
                    <td>{r.email}</td>
                    <td>
                      <span className={`lead-sender__badge lead-sender__badge--${r.status}`}>
                        {r.status === 'sent' ? '발송' : r.status === 'skipped' ? '스킵' : '오류'}
                      </span>
                    </td>
                    <td>{r.reason || r.actions?.join(', ') || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button className="lead-sender__reset-btn" onClick={handleReset}>
            새 CSV 발송
          </button>
        </div>
      )}
    </div>
  );
};

export default LeadSender;
